import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SmartImage from '@/components/SmartImage';

const useMobileAutoScroll = (railRef, enabled, productCount) => {
  useEffect(() => {
    const rail = railRef.current;
    const isMobile = window.matchMedia('(max-width: 767px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!enabled || !productCount || !rail || !isMobile.matches || reduceMotion.matches) return undefined;

    let direction = 1;
    let pauseUntil = 0;
    const pause = () => { pauseUntil = performance.now() + 4500; };
    const advance = () => {
      if (performance.now() <= pauseUntil) return;
      const maxScroll = rail.scrollWidth - rail.clientWidth;
      if (maxScroll <= 0) return;
      if (rail.scrollLeft >= maxScroll - 1) direction = -1;
      if (rail.scrollLeft <= 1) direction = 1;
      rail.scrollLeft = Math.max(0, Math.min(maxScroll, rail.scrollLeft + direction));
    };

    rail.addEventListener('touchstart', pause, { passive: true });
    rail.addEventListener('pointerdown', pause);
    rail.addEventListener('wheel', pause, { passive: true });
    const intervalId = window.setInterval(advance, 50);
    return () => {
      window.clearInterval(intervalId);
      rail.removeEventListener('touchstart', pause);
      rail.removeEventListener('pointerdown', pause);
      rail.removeEventListener('wheel', pause);
    };
  }, [enabled, productCount, railRef]);
};

export const ProductRail = ({
  autoScroll = false,
  cardTestIdPrefix,
  carouselTestId,
  eyebrow,
  formatPrice,
  lang,
  products,
  sectionTestId,
  title,
  mobileCards = 1,
  imageOnly = false,
  containImages = false,
  transparentImageFrame = false,
  largerMobileCards = false,
  subtitle,
  onProductClick,
  compactTop = false,
  surfaceClass = 'bg-white',
  desktopColumns = 3,
}) => {
  const railRef = useRef(null);
  useMobileAutoScroll(railRef, autoScroll, products.length);
  const mobileCardClass = mobileCards === 3
    ? (largerMobileCards ? 'w-[calc((100%-1rem)/3)] sm:w-[calc((100%-1rem)/3)]' : 'w-[calc((100%-2rem)/3)] sm:w-[calc((100%-2rem)/3)]')
    : 'w-[78vw] sm:w-[48vw]';

  if (!products.length) return null;

  return (
    <section className={`${compactTop ? 'pt-12 pb-24 md:py-32' : 'py-24 md:py-32'} ${surfaceClass}`} data-testid={sectionTestId}>
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-14 md:mb-20">
          <div className="overline text-[#C9A961] mb-4" data-testid={`${sectionTestId}-overline`}>{eyebrow}</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-[#0F3530]" data-testid={`${sectionTestId}-title`}>
            {title}
          </h2>
          {subtitle && <p className="overline text-[#0F3530] mt-4" data-testid={`${sectionTestId}-subtitle`}>{subtitle}</p>}
        </div>
        <div
          ref={railRef}
          className={`home-product-rail flex flex-nowrap ${largerMobileCards ? 'gap-2' : 'gap-4'} overflow-x-auto pb-4 snap-x snap-mandatory md:grid ${desktopColumns === 4 ? 'md:grid-cols-4 md:gap-6' : 'md:grid-cols-3 md:gap-12'} md:overflow-visible md:pb-0`}
          data-testid={carouselTestId}
          aria-label={title}
        >
          {products.map((product) => {
            const cardClass = `editorial-card group flex-none ${mobileCardClass} md:w-auto snap-start ${onProductClick ? 'text-left' : ''} ${containImages ? 'wave-bicolore-card' : ''}`;
            const mediaClass = `jewelry-media-surface aspect-[3/4] overflow-hidden ${transparentImageFrame ? 'wave-bicolore-image-frame' : 'bg-white'} ${imageOnly ? '' : 'mb-6'}`;
            const imageClass = `w-full h-full ${containImages ? 'object-contain' : 'object-cover'} editorial-img`;
            const cardContent = (
              <>
                <div className={mediaClass}>
                  <SmartImage src={product.images?.[0]} alt={product.sku} sizes="(min-width: 768px) 33vw, 78vw" className={imageClass} />
                </div>
                {!imageOnly && <>
                  <div className="overline editorial-overline text-[#4A4A4A] mb-2">{product.gem_type?.replace('_', ' ')}</div>
                  <h3 className="font-serif text-2xl text-[#0F3530] mb-2">{product.translations?.[lang]?.name || product.sku}</h3>
                  <div>
                    <div className="text-sm text-[#C9A961] font-medium">{formatPrice(product.price)}</div>
                    <div className="mt-1 text-[10px] text-[#4A4A4A]" data-testid={`${cardTestIdPrefix}-${product.slug}-price-inclusions`}>{lang === 'it' ? 'IVA e spedizione incluse' : 'VAT and shipping included'}</div>
                  </div>
                </>}
              </>
            );

            return onProductClick ? (
              <button
                type="button"
                key={product.id}
                className={cardClass}
                onClick={() => onProductClick(product)}
                aria-label={product.translations?.[lang]?.name || product.sku}
                data-testid={`${cardTestIdPrefix}-${product.slug}`}
              >
                {cardContent}
              </button>
            ) : (
              <Link to={`/prodotto/${product.slug}`} key={product.id} className={cardClass} data-testid={`${cardTestIdPrefix}-${product.slug}`}>
                {cardContent}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};